/**
 * Pure graph operations on a `WorkflowDefinition`.
 *
 * Used by both `WorkflowBuilderPanel` (canvas editor) and the wizard's
 * `FlowStepsPanel` (linear step list). Living here, not inside a
 * component, so the semantics are unit-tested and the two surfaces can't
 * drift apart.
 *
 * All operations are immutable: take a definition, return a new one.
 * The input is never mutated.
 */
import type { WorkflowDefinition, WorkflowEdge, WorkflowNode } from '@corner/shared';

function deepClone<T>(value: T): T {
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value)) as T;
}

function randomEdgeId(): string {
  const suffix = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? (crypto as Crypto & { randomUUID: () => string }).randomUUID().slice(0, 8)
    : Math.random().toString(36).slice(2, 10);
  return `edge_${suffix}`;
}

/**
 * Move a node from `fromIndex` to `toIndex` in the linear node order, then
 * rewrite ALL edges so the linear flow A→B→C→...→Z follows the new order.
 *
 * This loses any non-linear topology (branches, loops). The wizard's
 * `FlowStepsPanel` only renders linear flows, and reordering inside a
 * branched graph would be ambiguous anyway. If/when branched reordering
 * is needed, this function needs a smarter rewire strategy.
 *
 * Updates `entryNodeId` to track the new first node.
 */
export function reorderSteps(
  def: WorkflowDefinition,
  fromIndex: number,
  toIndex: number,
): WorkflowDefinition {
  const next = deepClone(def);
  const nodes = next.graph.nodes;
  if (fromIndex < 0 || fromIndex >= nodes.length) return next;
  if (toIndex < 0 || toIndex >= nodes.length) return next;
  if (fromIndex === toIndex) return next;

  const [moved] = nodes.splice(fromIndex, 1);
  nodes.splice(toIndex, 0, moved);

  next.graph.entryNodeId = nodes[0]?.nodeId ?? next.graph.entryNodeId;
  next.graph.edges = nodes.slice(0, -1).map<WorkflowEdge>((n, i) => ({
    edgeId: `e_${n.nodeId}_${nodes[i + 1].nodeId}`,
    sourceNodeId: n.nodeId,
    targetNodeId: nodes[i + 1].nodeId,
    condition: null,
  }));

  return next;
}

/**
 * Remove a node and auto-reconnect each incoming edge to each outgoing
 * edge (so deleting B in A→B→C produces A→C).
 *
 * REFUSES to delete the entry node — matches the existing
 * `WorkflowBuilderPanel.deleteNode` contract. Removing the entry would
 * leave the workflow without a starting point; entry reassignment
 * deserves its own explicit operation.
 */
export function removeStep(
  def: WorkflowDefinition,
  nodeId: string,
): WorkflowDefinition {
  if (nodeId === def.graph.entryNodeId) return def;
  const next = deepClone(def);
  const idx = next.graph.nodes.findIndex((n) => n.nodeId === nodeId);
  if (idx === -1) return next;

  const incoming = next.graph.edges.filter((e) => e.targetNodeId === nodeId);
  const outgoing = next.graph.edges.filter((e) => e.sourceNodeId === nodeId);

  for (const inc of incoming) {
    for (const out of outgoing) {
      next.graph.edges.push({
        edgeId: randomEdgeId(),
        sourceNodeId: inc.sourceNodeId,
        targetNodeId: out.targetNodeId,
        condition: null,
      });
    }
  }

  next.graph.nodes = next.graph.nodes.filter((n) => n.nodeId !== nodeId);
  next.graph.edges = next.graph.edges.filter(
    (e) => e.sourceNodeId !== nodeId && e.targetNodeId !== nodeId,
  );
  return next;
}

/**
 * Insert a new node immediately after `afterNodeId` and rewire edges so
 * any flow that used to go A→B now goes A→new→B.
 *
 * Mirrors the existing `WorkflowBuilderPanel.insertNodeAfter` semantics.
 * Returns the input unchanged if `afterNodeId` isn't present.
 */
export function insertStepAfter(
  def: WorkflowDefinition,
  afterNodeId: string,
  newNode: WorkflowNode,
): WorkflowDefinition {
  const next = deepClone(def);
  const exists = next.graph.nodes.some((n) => n.nodeId === afterNodeId);
  if (!exists) return next;

  next.graph.nodes.push(newNode);

  const existingOutgoing = next.graph.edges.filter((e) => e.sourceNodeId === afterNodeId);
  if (existingOutgoing.length > 0) {
    // Rewire only the FIRST outgoing edge through the new node.
    // Matches WorkflowBuilderPanel.insertNodeAfter exactly — the existing
    // builder relies on this single-rewire behaviour for branch nodes.
    const firstOldEdge = existingOutgoing[0];
    firstOldEdge.sourceNodeId = newNode.nodeId;
  }
  next.graph.edges.push({
    edgeId: randomEdgeId(),
    sourceNodeId: afterNodeId,
    targetNodeId: newNode.nodeId,
    condition: null,
  });

  return next;
}
